import { useMemo, useState } from "react";
import { Users, Minus, Plus, RotateCcw, Wheat } from "lucide-react";
import { parseIngredient } from "@/lib/scaling";

// PortionsScaler — permite escalar una receta por:
//   1) # de personas (multiplicador = nuevaPorc / porcOriginal)
//   2) Cantidad de un ingrediente clave (ej. "500 g de harina")
//      → multiplicador = nuevoValor / valorOriginal
//
// Props:
//   originalPorciones (number)  — porciones originales de la receta
//   ingredientes (string[])      — ingredientes originales
//   factor (number)              — factor actual
//   onFactorChange (fn)          — callback pa' actualizar el factor
//
// Encuentra los ingredientes "escalables" (parseables) y los ofrece como
// "ingrediente clave" opcional (típicamente harina, azúcar, líquido base).

const KEY_INGREDIENT_HINTS = /(harina|az[uú]car|arroz|le[ck]he|agua|masa|manteca|mantequilla|aceite|papa|papas)/i;

export default function PortionsScaler({
  originalPorciones,
  ingredientes = [],
  factor,
  onFactorChange,
}) {
  const [mode, setMode] = useState("personas"); // "personas" | "ingrediente"
  const [keyIngIdx, setKeyIngIdx] = useState(null);
  const [keyTargetQty, setKeyTargetQty] = useState("");

  // Lista de ingredientes parseables (candidatos a "clave")
  const parseable = useMemo(() => {
    const out = [];
    ingredientes.forEach((line, idx) => {
      const p = parseIngredient(typeof line === "string" ? line : String(line));
      if (!p.unparseable) out.push({ idx, ...p, line });
    });
    return out;
  }, [ingredientes]);

  // Sugerencia inteligente del "ingrediente clave" (primero que matchee harina/azúcar/etc.)
  const suggestedKeyIdx = useMemo(() => {
    const hit = parseable.find((p) => KEY_INGREDIENT_HINTS.test(p.orig));
    return hit ? hit.idx : (parseable[0]?.idx ?? null);
  }, [parseable]);

  const currentPersonas = Math.max(1, Math.round(originalPorciones * factor));
  const step = (delta) => {
    const nextP = Math.max(1, currentPersonas + delta);
    onFactorChange(nextP / originalPorciones);
  };

  const setPersonas = (n) => {
    const nn = Math.max(1, Math.min(50, parseInt(n, 10) || 0));
    if (nn > 0) onFactorChange(nn / originalPorciones);
  };

  const applyKeyIngredient = () => {
    const idx = keyIngIdx ?? suggestedKeyIdx;
    if (idx == null) return;
    const p = parseIngredient(typeof ingredientes[idx] === "string" ? ingredientes[idx] : String(ingredientes[idx]));
    if (p.unparseable) return;
    const target = parseFloat(String(keyTargetQty).replace(",", "."));
    if (!isFinite(target) || target <= 0) return;
    onFactorChange(target / p.qty);
  };

  const reset = () => onFactorChange(1);

  const factorLabel = factor === 1 ? "Original" : `× ${factor.toFixed(2).replace(/\.?0+$/, "")}`;
  const currentKeyIdx = keyIngIdx ?? suggestedKeyIdx;
  const currentKey = currentKeyIdx != null ? parseable.find((p) => p.idx === currentKeyIdx) : null;

  return (
    <div
      data-testid="portions-scaler"
      className="bg-cream/70 border border-amber-deep/30 rounded-xl p-4 sm:p-5 mb-6"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-deep" />
          <p className="eyebrow">Ajustar cantidades</p>
        </div>
        {factor !== 1 && (
          <button
            data-testid="scaler-reset-btn"
            onClick={reset}
            className="text-xs font-serif italic text-amber-deep flex items-center gap-1 hover:underline"
          >
            <RotateCcw className="w-3 h-3" /> Volver al original ({originalPorciones} personas)
          </button>
        )}
      </div>

      {/* Selector de modo */}
      <div className="flex gap-2 mb-4">
        <button
          data-testid="scaler-mode-personas"
          onClick={() => setMode("personas")}
          className={`flex-1 px-3 py-2 rounded-full font-serif text-sm transition border ${
            mode === "personas" ? "bg-amber-deep text-cream border-amber-deep" : "bg-paper text-cocoa border-line hover:border-amber-deep"
          }`}
        >
          Por personas
        </button>
        {parseable.length > 0 && (
          <button
            data-testid="scaler-mode-ingrediente"
            onClick={() => setMode("ingrediente")}
            className={`flex-1 px-3 py-2 rounded-full font-serif text-sm transition border ${
              mode === "ingrediente" ? "bg-amber-deep text-cream border-amber-deep" : "bg-paper text-cocoa border-line hover:border-amber-deep"
            }`}
          >
            Por ingrediente
          </button>
        )}
      </div>

      {mode === "personas" && (
        <div className="flex items-center gap-3">
          <button
            data-testid="scaler-minus-btn"
            onClick={() => step(-1)}
            disabled={currentPersonas <= 1}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-paper border border-line text-cocoa disabled:opacity-30 hover:border-amber-deep transition"
            aria-label="Menos personas"
          >
            <Minus className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center">
            <input
              data-testid="scaler-personas-input"
              type="number"
              min="1"
              max="50"
              value={currentPersonas}
              onChange={(e) => setPersonas(e.target.value)}
              className="w-20 text-center font-display italic text-3xl text-cocoa bg-transparent border-none focus:outline-none"
            />
            <p className="text-xs font-serif italic text-cocoa-soft -mt-1">
              personas · <span data-testid="scaler-factor-label">{factorLabel}</span>
            </p>
          </div>
          <button
            data-testid="scaler-plus-btn"
            onClick={() => step(1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-paper border border-line text-cocoa hover:border-amber-deep transition"
            aria-label="Más personas"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}

      {mode === "ingrediente" && (
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-serif italic text-cocoa-soft flex items-center gap-1 mb-1">
              <Wheat className="w-3 h-3" /> Ingrediente clave
            </span>
            <select
              data-testid="scaler-key-select"
              value={currentKeyIdx ?? ""}
              onChange={(e) => setKeyIngIdx(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 rounded-md bg-paper border border-line text-cocoa font-serif text-sm focus:outline-none focus:ring-2 focus:ring-amber-deep/40"
            >
              {parseable.map((p) => (
                <option key={p.idx} value={p.idx}>{p.orig}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-serif italic text-cocoa-soft mb-1 block">
              ¿Cuánto vas a usar? {currentKey && (
                <span>(original: <b>{currentKey.qty}{currentKey.unit ? ` ${currentKey.unit}` : ""}</b>)</span>
              )}
            </span>
            <div className="flex gap-2">
              <input
                data-testid="scaler-key-target"
                type="text"
                inputMode="decimal"
                value={keyTargetQty}
                onChange={(e) => setKeyTargetQty(e.target.value)}
                placeholder={currentKey ? `Ej. ${currentKey.qty * 2}` : "Cantidad"}
                className="flex-1 px-3 py-2 rounded-md bg-paper border border-line text-cocoa font-serif focus:outline-none focus:ring-2 focus:ring-amber-deep/40"
              />
              <button
                data-testid="scaler-apply-btn"
                onClick={applyKeyIngredient}
                className="btn-amber whitespace-nowrap"
              >
                Recalcular
              </button>
            </div>
          </label>
          {factor !== 1 && (
            <p className="text-xs italic text-cocoa-soft">
              Ahora rinde <b data-testid="scaler-personas-result">{currentPersonas}</b> personas · <span data-testid="scaler-factor-label-ing">{factorLabel}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
